import { useGetTripSummaryQuery } from "@/redux/services/allocationApi";
import {
	Alert,
	Button,
	Card,
	Flex,
	Result,
	Spin,
	Tag,
	Timeline,
	Typography,
} from "antd";
import {
	CarOutlined,
	ClockCircleOutlined,
	MessageOutlined,
	PhoneOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import "./style.css";

const { Title, Text } = Typography;
const dateFormat = "YYYY-MM-DD";

const formatValue = (value, fallback = "-") => {
	if (value === null || value === undefined || value === "") {
		return fallback;
	}
	return value;
};

const normalizePlate = (value) => {
	return String(formatValue(value, ""))
		.trim()
		.toUpperCase();
};

const getPeopleNo = (item) => {
	const peopleNo = Number(item.people_no);

	return Number.isFinite(peopleNo) && peopleNo > 0 ? peopleNo : 1;
};

const sortByPickupTime = (items = []) => {
	return [...items].sort((a, b) =>
		formatValue(a.pickup_time, "").localeCompare(formatValue(b.pickup_time, ""))
	);
};

const groupPickupStops = (items = []) => {
	const groupedStops = {};

	sortByPickupTime(items).forEach((item) => {
		const key = [
			formatValue(item.pickup_time, ""),
			formatValue(item.pickup_location, ""),
			formatValue(item.pickup_address, ""),
		].join("|");

		if (!groupedStops[key]) {
			groupedStops[key] = {
				pickup_time: item.pickup_time,
				pickup_location: item.pickup_location,
				pickup_address: item.pickup_address,
				clients: [],
				people_no: 0,
			};
		}

		groupedStops[key].clients.push(item);
		groupedStops[key].people_no += getPeopleNo(item);
	});

	return Object.values(groupedStops);
};

const findBusAllocations = (productList = [], vehiclePlate) => {
	const targetPlate = normalizePlate(vehiclePlate);
	const matches = [];

	productList.forEach((product) => {
		(product.allocation_list || []).forEach((allocation) => {
			if (
				normalizePlate(allocation.vehicle?.vehicle_plate_number) ===
				targetPlate
			) {
				matches.push({
					product,
					allocation,
				});
			}
		});
	});

	return matches;
};

const getPhoneHrefValue = (phone) => {
	const value = String(formatValue(phone, "")).replace(/[\s()-]/g, "");

	if (!value) {
		return "";
	}

	if (value.startsWith("+")) {
		return value;
	}

	if (value.startsWith("00")) {
		return `+${value.slice(2)}`;
	}

	if (!value.startsWith("0")) {
		return `+${value}`;
	}

	return value;
};

function PhoneActionLinks({ phone }) {
	const phoneHrefValue = getPhoneHrefValue(phone);

	if (!phoneHrefValue) {
		return null;
	}

	return (
		<Flex className="bus-detail-phone-actions" align="center" gap={6} wrap="wrap">
			<Text>{formatValue(phone)}</Text>
			<Button
				href={`tel:${phoneHrefValue}`}
				icon={<PhoneOutlined />}
				size="small"
				type="link"
			>
				打电话
			</Button>
			<Button
				href={`sms:${phoneHrefValue}`}
				icon={<MessageOutlined />}
				size="small"
				type="link"
			>
				发短信
			</Button>
		</Flex>
	);
}

function BusAllocationDetail({ allocation, product }) {
	const vehicle = allocation.vehicle || {};
	const sortedItems = useMemo(
		() => sortByPickupTime(allocation.items || []),
		[allocation.items]
	);
	const stops = useMemo(() => groupPickupStops(sortedItems), [sortedItems]);
	const totalPeople = sortedItems.reduce(
		(total, item) => total + getPeopleNo(item),
		0
	);
	const timelineItems = stops.map((stop) => ({
		dot: <ClockCircleOutlined />,
		color: product.product_color,
		label: (
			<div className="bus-detail-timeline-place">
				<Tag color="blue">{formatValue(stop.pickup_time)}</Tag>
				<Text strong>{formatValue(stop.pickup_location)}</Text>
				<Text type="secondary">{stop.pickup_address || "\u00A0"}</Text>
			</div>
		),
		children: (
			<div className="bus-detail-timeline-passengers">
				<Tag color="green">共 {stop.people_no} 人</Tag>
				{stop.clients.map((client) => (
					<div
						className="bus-detail-timeline-passenger"
						key={client.allocation_uuid || client.order_sub_id || client.client_name}
					>
						<div className="bus-detail-passenger-main">
							<Text strong>{formatValue(client.client_name)}</Text>
							<Tag color="default">{getPeopleNo(client)} 人</Tag>
						</div>
						<Text strong>渠道：{formatValue(client.user_name)}</Text>
						{client.client_phone && (
							<PhoneActionLinks phone={client.client_phone} />
						)}
						{client.comment && (
							<Text className="bus-detail-passenger-comment" type="secondary">
								备注：{client.comment}
							</Text>
						)}
					</div>
				))}
			</div>
		),
	}));

	return (
		<Card
			className="bus-detail-card"
			title={
				<Flex align="center" gap={10} wrap="wrap">
					<CarOutlined />
					<span>{formatValue(vehicle.vehicle_plate_number)}</span>
					<Tag color={product.product_color}>{product.product_title}</Tag>
					<Tag color="orange">共 {totalPeople} 人</Tag>
				</Flex>
			}
		>
			<div className="bus-detail-timeline">
				<Timeline mode="left" items={timelineItems} />
			</div>
		</Card>
	);
}

function BusDetailPage() {
	const { vehiclePlate } = useParams();
	const { data, isError, isLoading } = useGetTripSummaryQuery();

	const matches = useMemo(() => {
		if (!data?.product_list || !vehiclePlate) {
			return [];
		}

		return findBusAllocations(data.product_list, vehiclePlate);
	}, [data, vehiclePlate]);

	const selectedDate = data?.travel_date
		? dayjs(data.travel_date).format(dateFormat)
		: "";

	return (
		<div className="bus-detail-page">
			<Flex
				className="bus-detail-header"
				justify="space-between"
				align="center"
				wrap="wrap"
				gap={12}
			>
				<div>
					<Title level={2} className="bus-detail-title">
						司机接人信息
					</Title>
					<Text type="secondary">
						{selectedDate} · 车牌 {formatValue(vehiclePlate)}
					</Text>
				</div>
			</Flex>

			{isError && (
				<Alert
					message="Error"
					description="Something went wrong ..."
					type="error"
					showIcon
				/>
			)}

			{isLoading && (
				<Flex justify="center" align="center" className="bus-detail-loading">
					<Spin size="large" />
				</Flex>
			)}

			{!isLoading && matches.length === 0 && (
				<Result
					status="404"
					title="No Data"
					subTitle="没有找到对应车牌的接人信息"
				/>
			)}

			{matches.map(({ product, allocation }) => (
				<BusAllocationDetail
					key={`${product.product_id}-${allocation.list_name || allocation.vehicle?.vehicle_plate_number}`}
					allocation={allocation}
					product={product}
				/>
			))}
		</div>
	);
}

export default BusDetailPage;
